import { type MultiStateDropdownOption } from "components/dropdowns/multiStateDropdown/MultiStateDropdown";
import { getAssetsMultiStateOptions } from "features/Library/Flows/Designer/FlowComponentsPanel/contentView/PanelComponent/helpers/assetsOptions";
import { type ReactElement } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faMessage, faShieldCheck } from "@fortawesome/pro-solid-svg-icons";
import { RolePermissions } from "../../../../../enums";
import StaticAssetCard from "../../../../Library/Flows/Designer/FlowComponentsPanel/dragndropCards/StaticAssetCard";
import { type CardsViewerItem } from "../../../../../components/cardsViewer/types";
import DesignerAssetCard from "components/cards/DesignerAssetCard/DesignerAssetCard";
import { communicationsOptions } from "./communicationsOptions";
import { EntityType } from "features/Library/Flows/Designer/types";
import ThreatDefenceContentTypes from "../../../../../enums/threatDefenceContentTypes";
import DragNDropItemTypes from "enums/dragNDropItemTypes";

interface TabsOptions {
  label: string;
  filtersId: string;
  tabId: string;
  permissions: RolePermissions[];
  filterOptions?: MultiStateDropdownOption<string>[];
  renderCard: (isReadOnly: boolean) => (props: CardsViewerItem<any>) => ReactElement;
}

export const getContentViewTabsOptions = (
  embeddedVideosEnabled: boolean = false,
  isTDAddonEnabled: boolean = false,
  linksEnabled: boolean = false,
  scormEnabled: boolean = false,
): TabsOptions[] => {
  const options = [
    {
      label: "Content",
      filtersId: "pack-assets-to-drop-filter",
      tabId: "add-assets-to-pack-panel",
      permissions: [RolePermissions.AssetsView],
      filterOptions: getAssetsMultiStateOptions(embeddedVideosEnabled, linksEnabled, isTDAddonEnabled, scormEnabled),
      renderCard: (isReadOnly: boolean) => (props: CardsViewerItem<any>) => {
        if (props.item.cardType === EntityType.Campaign) {
          return (
            <DesignerAssetCard
              dataHookType="flow-designer-campaign-card"
              isDraggable={!isReadOnly}
              assetType={ThreatDefenceContentTypes.Campaign}
              thumbnailUrl={<FontAwesomeIcon icon={faShieldCheck} size="lg" className="icon" />}
              {...props.item}
              dragNDropItemType={DragNDropItemTypes.PACK}
            />
          );
        }
        return (
          <StaticAssetCard
            isDraggable={!isReadOnly}
            item={{ ...props.item, dragNDropItemType: DragNDropItemTypes.PACK }}
          />
        );
      },
    },
    {
      label: "Flows",
      filtersId: "pack-flows-to-drop-filter",
      tabId: "add-flows-to-pack-panel",
      permissions: [RolePermissions.FlowsView],
      renderCard: (isReadOnly: boolean) => (props: CardsViewerItem<any>) => (
        <DesignerAssetCard
          dataHookType="flow-designer-flow-asset-card"
          isDraggable={!isReadOnly}
          assetType={EntityType.Flow}
          id={props.item.id}
          title={props.item.title}
          thumbnailUrl={props.item.thumbnailUrl}
          dragNDropItemType={DragNDropItemTypes.PACK}
          visibility={!props.item.hidden}
          publisher={props.item.publisher}
        />
      ),
    },
    {
      label: "Communications",
      filtersId: "pack-communications-to-drop-filter",
      tabId: "add-communications-to-pack-panel",
      filterOptions: communicationsOptions,
      permissions: [RolePermissions.CommunicationsView],
      renderCard: (isReadOnly: boolean) => (props: CardsViewerItem<any>) => {
        const assetType = {
          Email: EntityType.Email,
          Message: EntityType.Message,
        };

        const icons = {
          Email: <FontAwesomeIcon icon={faEnvelope} size="lg" className="icon" />,
          Message: <FontAwesomeIcon icon={faMessage} size="lg" className="icon" />,
        };

        return (
          <DesignerAssetCard
            isDraggable={!isReadOnly}
            thumbnailUrl={icons[props.item.contentType as keyof typeof icons]}
            dataHookType={"flow-designer-communication-card"}
            assetType={assetType[props.item.contentType as keyof typeof assetType]}
            id={props.item.id}
            title={props.item.title}
            dragNDropItemType={DragNDropItemTypes.PACK}
            publisher={props.item.publisher}
          />
        );
      },
    },
    {
      label: "Events",
      filtersId: "pack-events-to-drop-filter",
      tabId: "add-events-to-pack-panel",
      permissions: [RolePermissions.EventsView],
      renderCard: (isReadOnly: boolean) => (props: CardsViewerItem<any>) => (
        <DesignerAssetCard
          isDraggable={!isReadOnly}
          id={props.item.id}
          title={props.item.title}
          assetType={EntityType.ExternalEvent}
          thumbnailUrl={props.item.thumbnailUrl}
          dataHookType="event-to-drop-card"
          dragNDropItemType={DragNDropItemTypes.PACK}
          hasReminders={props.item.hasReminders}
          visibility={props.item.visibility}
          publisher={props.item.publisher}
        />
      ),
    },
  ];

  return options;
};
